const HISTORY_KEY = 'ombre:product-history:v2';
const HISTORY_TTL = 30 * 24 * 60 * 60 * 1000;

const normalizeToken = (value, fallback = 'other') => {
  const token = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return token || fallback;
};

const stableNumber = (value) => {
  let hash = 2166136261;
  for (const character of String(value || '')) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
};

export const rankRecommendations = (candidates, context = {}) => {
  const limit = Math.max(1, Number.parseInt(context.limit || 8, 10));
  const currentHandle = normalizeToken(context.currentHandle, '');
  const currentCategory = normalizeToken(context.currentCategory);
  const seed = String(context.seed || 'ombre-noir');
  const history = Array.isArray(context.history) ? context.history : [];
  const categoryAffinity = new Map();

  history.slice(0, 12).forEach((entry, index) => {
    const category = normalizeToken(entry?.category);
    const recencyWeight = Math.max(1, 12 - index) * (entry?.action === 'click' ? 1.5 : 1);
    categoryAffinity.set(category, (categoryAffinity.get(category) || 0) + recencyWeight);
  });

  const unique = [];
  const seenHandles = new Set();
  for (const candidate of Array.isArray(candidates) ? candidates : []) {
    const handle = normalizeToken(candidate?.handle, '');
    if (!handle || handle === currentHandle || seenHandles.has(handle)) continue;
    seenHandles.add(handle);
    unique.push({ ...candidate, handle, category: normalizeToken(candidate?.category) });
  }

  const ranked = unique
    .map((candidate) => {
      const relevance = candidate.category === currentCategory ? 26 : 0;
      const affinity = (categoryAffinity.get(candidate.category) || 0) * 2.4;
      const discovery = stableNumber(`${seed}:${candidate.handle}`) * 8;
      return { candidate, score: relevance + affinity + discovery };
    })
    .sort((left, right) => right.score - left.score || left.candidate.handle.localeCompare(right.candidate.handle));

  const selected = [];
  const selectedHandles = new Set();
  const categoryCounts = new Map();
  const categoryCap = limit >= 6 ? Math.ceil(limit / 2) : limit;

  for (const entry of ranked) {
    if (selected.length >= limit) break;
    const categoryCount = categoryCounts.get(entry.candidate.category) || 0;
    if (categoryCount >= categoryCap) continue;
    selected.push(entry.candidate);
    selectedHandles.add(entry.candidate.handle);
    categoryCounts.set(entry.candidate.category, categoryCount + 1);
  }

  for (const entry of ranked) {
    if (selected.length >= limit) break;
    if (selectedHandles.has(entry.candidate.handle)) continue;
    selected.push(entry.candidate);
    selectedHandles.add(entry.candidate.handle);
  }

  return selected;
};

// Only consent-permitted, device-local interests; never customer identities or remote profiles.
export const personalizationAllowed = (privacy) => {
  try {
    return privacy?.preferencesProcessingAllowed() === true && privacy?.analyticsProcessingAllowed() === true;
  } catch { return false; }
};

export const readHistory = (storage, allowed, now = Date.now()) => {
  if (!allowed) return [];
  try {
    const entries = JSON.parse(storage.getItem(HISTORY_KEY) || '[]');
    if (!Array.isArray(entries)) return [];
    return entries.filter((entry) => entry && typeof entry.handle === 'string' &&
      typeof entry.category === 'string' && Number.isFinite(entry.at) && entry.at <= now &&
      now - entry.at < HISTORY_TTL).slice(0, 12);
  } catch { return []; }
};

export const recordInterest = (storage, allowed, entry, now = Date.now()) => {
  if (!allowed || !entry.handle) return;
  const history = readHistory(storage, true, now);
  const previous = history.find((item) => item.handle === entry.handle);
  const next = [{
    handle: normalizeToken(entry.handle, ''), category: normalizeToken(entry.category),
    action: entry.action === 'click' || previous?.action === 'click' ? 'click' : 'visit', at: now,
  }, ...history.filter((item) => item.handle !== entry.handle)].slice(0, 12);
  try { storage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* Storage may be blocked. */ }
};

export const collectCandidatePages = async (initialUrl, fetchPage, origin) => {
  const candidates = [];
  const visited = new Set();
  let next = initialUrl;
  let base = origin;
  while (next) {
    const url = new URL(next, base);
    if (url.origin !== origin || visited.has(url.href)) throw new Error('Invalid recommendation pagination');
    visited.add(url.href);
    const page = await fetchPage(url);
    candidates.push(...page.candidates);
    base = url.href;
    next = page.next;
  }
  return candidates;
};

let privacyReady;
const loadPrivacy = () => {
  if (!privacyReady) privacyReady = new Promise((resolve) => {
    if (window.Shopify?.customerPrivacy || !window.Shopify?.loadFeatures) return resolve();
    const timeout = setTimeout(resolve, 3000);
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      clearTimeout(timeout);
      resolve();
      if (!error) document.dispatchEvent(new Event('ombre:privacy-ready'));
    });
  });
  return privacyReady;
};
const storage = () => { try { return window.localStorage; } catch { return null; } };
const allowed = () => personalizationAllowed(window.Shopify?.customerPrivacy);
const cardCandidate = (card) => ({ handle: card.dataset.productHandle, category: card.dataset.productCategory, element: card });

const candidateRequests = new Map();
const loadCandidates = (collectionUrl) => {
  if (!candidateRequests.has(collectionUrl)) {
    const request = collectCandidatePages(collectionUrl, async (url) => {
      url.searchParams.set('section_id', 'ombre-recommendation-candidates');
      // The section inherits the collection, locale, currency and Online Store publication context.
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Recommendation candidates unavailable');
        const html = await response.text();
        // Parsing inside a template keeps every nonselected image inert (no network requests).
        const container = document.createElement('template');
        container.innerHTML = html;
        const page = container.content.querySelector('template[data-onpd-candidates]');
        if (!page) throw new Error('Recommendation section unavailable');
        return {
          candidates: Array.from(page.content.querySelectorAll('[data-onpd-recommendation-card]')).map(cardCandidate),
          next: page.dataset.nextUrl,
        };
      } finally { clearTimeout(timeout); }
    }, window.location.origin);
    candidateRequests.set(collectionUrl, request);
  }
  return candidateRequests.get(collectionUrl);
};

const initializeRecommendations = async (root) => {
  if (!root || root.dataset.onpdRecommendationsReady) return;
  const rail = root.querySelector('[data-onpd-recommendation-rail]');
  if (!rail) return;
  root.dataset.onpdRecommendationsReady = 'loading';
  const currentHandle = normalizeToken(root.dataset.currentHandle, '');
  const currentCategory = normalizeToken(root.dataset.currentCategory);
  let candidates = Array.from(rail.querySelectorAll('[data-onpd-recommendation-card]')).map(cardCandidate);
  const render = () => {
    if (!root.isConnected) return;
    const selected = rankRecommendations(candidates, {
      currentHandle, currentCategory,
      history: readHistory(storage(), allowed()),
      seed: currentHandle,
      limit: root.dataset.recommendationLimit || 8,
    });
    if (selected.length) rail.replaceChildren(...selected.map((item) => item.element.cloneNode(true)));
  };
  const rememberVisit = () => recordInterest(storage(), allowed(), { handle: currentHandle, category: currentCategory, action: 'visit' });
  await loadPrivacy();
  render();
  rememberVisit();
  const consentChanged = () => {
    if (!root.isConnected) {
      document.removeEventListener('visitorConsentCollected', consentChanged);
      document.removeEventListener('ombre:privacy-ready', consentChanged);
      return;
    }
    if (!allowed()) {
      try {
        storage()?.removeItem(HISTORY_KEY);
        storage()?.removeItem('ombre:product-history:v1');
        storage()?.removeItem('ombre:recommendation-seed:v1');
      } catch { /* Blocked storage must not break recommendations. */ }
    }
    render();
    rememberVisit();
  };
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('ombre:privacy-ready', consentChanged);
  rail.addEventListener('click', (event) => {
    const card = event.target.closest('[data-onpd-recommendation-card]');
    if (card) recordInterest(storage(), allowed(), { handle: card.dataset.productHandle, category: card.dataset.productCategory, action: 'click' });
  });
  try {
    const all = await loadCandidates(root.dataset.candidateUrl);
    if (all.length) candidates = all;
    render();
    root.dataset.onpdRecommendationsReady = 'true';
    root.dataset.candidateCount = String(candidates.length);
  } catch {
    // The server-rendered rail remains usable even if the section request is unavailable.
    root.dataset.onpdRecommendationsReady = 'fallback';
  }
};

if (typeof document !== 'undefined') {
  const initializeAllRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-onpd-recommendations]').forEach(initializeRecommendations);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAllRecommendations(), { once: true });
  } else { initializeAllRecommendations(); }
  document.addEventListener('shopify:section:load', (event) => initializeAllRecommendations(event.target));
}
